/**
 * Mopper · Architecture SEO "service × ville".
 *
 * Génère, à partir des données RÉELLES déjà présentes dans le dépôt
 * (SERVICES de seo.js + CITIES de citiesData.js), un descripteur par
 * combinaison service × ville, SANS inventer de contenu.
 *
 * Chaque combinaison reçoit un `qualityScore` (règle produit interne, PAS un
 * score Google). Seules les pages disposant de suffisamment de contenu réel et
 * différencié (score >= SEO_INDEX_THRESHOLD) sont indexables ; les autres
 * restent en noindex,follow (elles existent, restent maillées, mais ne sont pas
 * poussées dans l'index tant que du contenu local vérifié n'est pas ajouté).
 */
import { VISIBLE_SERVICES, SITE_URL } from "@/lib/seo";
import { CITIES, getCityBySlug } from "@/lib/citiesData";

export const SEO_INDEX_THRESHOLD = 70;

// Mots-clés permettant de détecter, dans la FAQ locale RÉELLE d'une ville, si le
// service concerné y est explicitement traité (différenciation locale vérifiable).
const SERVICE_FAQ_KEYWORDS = {
  move: ["déménagement", "demenagement", "déménager", "demenager"],
  clear: ["débarras", "debarras", "vider", "succession"],
  clean: ["nettoyage", "fin de bail", "état des lieux", "etat des lieux"],
  transport: ["transport", "livraison", "meuble"],
  organize: ["home organizing", "organizing", "organisation", "rangement", "dressing"],
};

function faqBlob(city) {
  return (city.faq || []).map((f) => `${f.q} ${f.a}`).join(" ").toLowerCase();
}

// FAQ réellement pertinentes pour ce service (sous-ensemble de la FAQ ville).
function relevantFaq(service, city) {
  const kws = SERVICE_FAQ_KEYWORDS[service.key] || [];
  return (city.faq || []).filter((f) => {
    const t = `${f.q} ${f.a}`.toLowerCase();
    return kws.some((k) => t.includes(k));
  });
}

/**
 * qualityScore interne (0–100), calculé UNIQUEMENT sur des données réelles.
 *  40 — contexte opérationnel local unique (localContext + quartiers + communes)
 *  20 — le service est explicitement traité dans la FAQ locale réelle
 *  15 — une indication de prix réelle (CHF) figure dans la FAQ locale
 *  10 — intention de recherche forte et récurrente (déménagement / débarras / nettoyage)
 *   5 — maillage local dense (>= 6 quartiers documentés)
 */
export function computeQualityScore(service, city) {
  let score = 0;
  const reasons = [];
  if (city.localContext && (city.neighborhoods || []).length && (city.nearbyCommunes || []).length) {
    score += 40; reasons.push("contexte local unique");
  }
  const relFaq = relevantFaq(service, city);
  if (relFaq.length > 0) { score += 20; reasons.push("FAQ locale spécifique au service"); }
  const relBlob = relFaq.map((f) => `${f.q} ${f.a}`).join(" ").toLowerCase();
  if (relFaq.length > 0 && /chf/.test(relBlob)) { score += 15; reasons.push("indication de prix réelle"); }
  if (["move", "clear", "clean"].includes(service.key)) { score += 10; reasons.push("intention locale forte"); }
  if ((city.neighborhoods || []).length >= 6) { score += 5; reasons.push("maillage quartiers dense"); }
  return { score: Math.min(score, 100), reasons };
}

function buildDescriptor(service, city) {
  const { score, reasons } = computeQualityScore(service, city);
  const indexable = score >= SEO_INDEX_THRESHOLD;
  const path = `/services/${service.slug}/${city.slug}`;
  const relFaq = relevantFaq(service, city);
  // FAQ affichée : d'abord les entrées réellement liées au service, complétées par
  // les autres questions locales réelles (aucune invention).
  const faq = relFaq.length >= 2 ? relFaq : (city.faq || []);
  return {
    serviceSlug: service.slug,
    serviceKey: service.key,
    serviceName: service.name,
    citySlug: city.slug,
    cityName: city.name,
    canton: city.canton,
    cantonSlug: city.cantonSlug,
    path,
    url: `${SITE_URL}${path}`,
    h1: `${service.name} à ${city.name}`,
    title: `${service.name} à ${city.name} | Mopper`,
    metaDescription:
      `${service.name} à ${city.name} (${city.canton}) avec Mopper : ${service.tagline} ` +
      `Devis clair et gratuit, intervention soignée.`.slice(0, 320),
    intro: service.intro,
    tagline: service.tagline,
    advantages: service.advantages || [],
    options: service.options || [],
    useCases: service.useCases || [],
    localContext: city.localContext,
    neighborhoods: city.neighborhoods || [],
    nearbyCommunes: city.nearbyCommunes || [],
    faq,
    qualityScore: score,
    qualityReasons: reasons,
    indexable,
  };
}

// Toutes les combinaisons service (public) × ville.
export const LOCAL_PAGES = [];
for (const service of VISIBLE_SERVICES) {
  for (const city of CITIES) {
    LOCAL_PAGES.push(buildDescriptor(service, city));
  }
}

export const INDEXABLE_LOCAL_PAGES = LOCAL_PAGES.filter((p) => p.indexable);

export function getLocalPage(serviceSlug, citySlug) {
  return (
    LOCAL_PAGES.find((p) => p.serviceSlug === serviceSlug && p.citySlug === citySlug) || null
  );
}

// Villes indexables pour un service donné (utilisé pour le maillage interne
// depuis la page service parente vers ses meilleures pages locales).
export function indexableCitiesForService(serviceSlug) {
  return LOCAL_PAGES.filter((p) => p.serviceSlug === serviceSlug && p.indexable);
}

// Services indexables pour une ville donnée (maillage depuis la page ville hub).
export function indexableServicesForCity(citySlug) {
  return LOCAL_PAGES.filter((p) => p.citySlug === citySlug && p.indexable);
}

export { getCityBySlug };
